import furaIcon from './icons/fura.svg';
import hagIcon from './icons/hag.svg';
import tcsIcon from './icons/tcs.svg';
import trucpalIcon from './icons/trucpal.svg';

export interface ICryptoAssetRegistryEntry {
  /** Full asset name for the card's second line, e.g. "Bitcoin". */
  name: string;
  /**
   * Either a CoinGecko URL or a bundled asset from `./icons`, which is where
   * the INX-only securities get their artwork.
   *
   * Omitted where no artwork exists at all. `AssetIcon` then renders an
   * initials circle, which is strictly better than the alternative: a wrong URL
   * degrades to an anonymous generic coin glyph, so never guess one.
   */
  logoUrl?: string;
}

const COINGECKO_IMAGE_BASE = 'https://coin-images.coingecko.com/coins/images';

/**
 * `/large/` rather than the `/small/` bucket the size name suggests: the card's
 * circle is 40px and CoinGecko's small variant is a 48px file, so on a 2x screen
 * it is upscaled and soft. Every bucket serves the same filename, so the size is
 * this one segment — which is why it lives here rather than in thirteen literals.
 */
const coinGeckoLogo = (id: number, file: string) =>
  `${COINGECKO_IMAGE_BASE}/${id}/large/${file}`;

/**
 * Display metadata per asset, keyed by the symbol {@link extractBaseSymbol}
 * produces — uppercase, no `-USD`.
 *
 * The ticker feed carries no names or logos, so both live here. Logos are
 * hotlinked from CoinGecko; committing the artwork is the eventual fix, since
 * this makes a Republic surface depend on a third party's CDN.
 */
export const CRYPTO_ASSET_REGISTRY: Readonly<
  Record<string, ICryptoAssetRegistryEntry | undefined>
> = {
  BTC: {
    name: 'Bitcoin',
    logoUrl: coinGeckoLogo(1, 'bitcoin.png'),
  },
  ETH: {
    name: 'Ethereum',
    logoUrl: coinGeckoLogo(279, 'ethereum.png'),
  },
  LTC: {
    name: 'Litecoin',
    logoUrl: coinGeckoLogo(2, 'litecoin.png'),
  },
  USDC: {
    name: 'USD Coin',
    logoUrl: coinGeckoLogo(6319, 'USDC.png'),
  },
  USDT: {
    name: 'Tether',
    logoUrl: coinGeckoLogo(325, 'Tether.png'),
  },
  // The C-Chain market is what INX lists, so the ticker line says AVAXC. Only
  // the artwork is borrowed from plain AVAX.
  AVAXC: {
    name: 'Avalanche',
    logoUrl: coinGeckoLogo(12559, 'Avalanche_Circle_RedWhite_Trans.png'),
  },
  DOT: {
    name: 'Polkadot',
    logoUrl: coinGeckoLogo(12171, 'polkadot.jpg'),
  },
  XRP: {
    name: 'XRP',
    logoUrl: coinGeckoLogo(44, 'xrp-symbol-white-128.png'),
  },
  PYUSD: {
    name: 'PayPal USD',
    logoUrl: coinGeckoLogo(31212, 'PYUSD_Token_Logo_2x.png'),
  },
  SOL: {
    name: 'Solana',
    logoUrl: coinGeckoLogo(4128, 'solana.png'),
  },
  POL: {
    name: 'Polygon',
    logoUrl: coinGeckoLogo(32440, 'pol.png'),
  },
  NOTE: {
    name: 'Republic Note',
    logoUrl: coinGeckoLogo(33726, 'Note-Logo-2023.png'),
  },
  CC: {
    name: 'Canton',
    logoUrl: coinGeckoLogo(70468, 'Canton-Ticker_%281%29.png'),
  },

  // INX digital securities with no CoinGecko listing. Their artwork is bundled
  // from `./icons` instead. Names carry the card's second line; the four still
  // named after their ticker are awaiting product copy.
  // TODO: https://linear.app/republic/issue/USMKT-2166/fe-setup-opportunities-page - real names for HAG, FURA, TCS and TRUCPAL
  //
  // `ANIMOCA` and `NOTE` above stay listed even though the rail filters them
  // out (see `RWA_TICKER_SYMBOLS`): the feed still carries both markets, and
  // the registry is what maps every symbol it sends to a display name.
  ANIMOCA: { name: 'Animoca Brands' },
  HAG: { name: 'HAG', logoUrl: hagIcon },
  FURA: { name: 'FURA', logoUrl: furaIcon },
  TCS: { name: 'TCS', logoUrl: tcsIcon },
  TRUCPAL: { name: 'TRUCPAL', logoUrl: trucpalIcon },
};

/**
 * Falls back to the symbol as its own name so a market INX adds tomorrow still
 * renders — as an initials circle beside its ticker — rather than blanking.
 */
export const getCryptoAssetDisplay = (
  symbol: string
): ICryptoAssetRegistryEntry =>
  CRYPTO_ASSET_REGISTRY[symbol] ?? { name: symbol };
